/* Desk → bus contact: Announce (parents) and Call driver (phone + Messages ping).
   Runs on pear-payments (Render), not the undeployed Cloud Function. */

import { auth } from '../firebase';
import { isPaymentsApiConfigured, paymentsApiUrl } from '../utils/paymentsBaseUrl';

export type AnnounceResult = {
  ok: boolean;
  sent?: number;
  total?: number;
  message?: string;
  reason?: string;
};

export type CallDriverPerson = {
  uid: string;
  name: string;
  phone: string;
  conversationId?: string;
};

export type CallDriverResult = {
  ok: boolean;
  plate?: string;
  drivers: CallDriverPerson[];
  message?: string;
  reason?: string;
};

async function authHeaders(): Promise<Record<string, string> | null> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) return null;
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

/** Push + inbox announcement to every parent on the bus. */
export async function announceToBusParents(
  orgCode: string,
  busId: string,
  message: string,
): Promise<AnnounceResult> {
  if (!isPaymentsApiConfigured()) return { ok: false, message: 'Payments API is not configured.' };
  const headers = await authHeaders();
  if (!headers) return { ok: false, message: 'Sign in again, then retry.' };
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), 20000);
  try {
    const res = await fetch(paymentsApiUrl('/api/fleet/announce'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ orgCode, busId, message }),
      signal: ac.signal,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok && !json?.message) {
      return { ok: false, message: `Could not send (${res.status}).` };
    }
    return {
      ok: json?.ok === true,
      sent: json?.sent,
      total: json?.total,
      message: json?.message,
      reason: json?.reason,
    };
  } catch (e) {
    const aborted = e instanceof Error && e.name === 'AbortError';
    return { ok: false, message: aborted ? 'That send did not finish, so I stopped it.' : 'Network error — try again.' };
  } finally {
    clearTimeout(timer);
  }
}

/** Notify linked drivers in Messages and return their phone numbers for dialling. */
export async function callBusDriver(
  orgCode: string,
  busId: string,
  adminName?: string,
): Promise<CallDriverResult> {
  if (!isPaymentsApiConfigured()) return { ok: false, drivers: [], message: 'Payments API is not configured.' };
  const headers = await authHeaders();
  if (!headers) return { ok: false, drivers: [], message: 'Sign in again, then retry.' };
  try {
    const res = await fetch(paymentsApiUrl('/api/fleet/call-driver'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ orgCode, busId, adminName }),
    });
    const json = await res.json().catch(() => ({}));
    return {
      ok: json?.ok === true,
      plate: json?.plate,
      drivers: Array.isArray(json?.drivers) ? json.drivers : [],
      message: json?.message,
      reason: json?.reason,
    };
  } catch {
    return { ok: false, drivers: [], message: 'Network error — try again.' };
  }
}
